import React, { useMemo, useState } from "react";
import {
    ChevronDown,
    GripVertical,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
    TASK_BOARD_COLUMNS,
    STAFF_PAUSE_REASONS,
    taskBoardColumnKey,
} from "../constants";
import { isOverdue } from "../utils/taskDates";
import { isRecurringTask } from "../utils/taskReminderSync";
import { assigneeDisplayName } from "../utils/taskCardMeta";
import { TaskCardMeta } from "./TaskCardMeta";
import PauseReasonModal from "./PauseReasonModal";
import CompleteProofModal from "./CompleteProofModal";

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
const COLUMN_PAGE = 40;

const sortColumnTasks = (list, sortBy) => {
    const next = [...list];
    if (sortBy === "due") {
        return next.sort((a, b) => {
            const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
            const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
            return da - db;
        });
    }
    if (sortBy === "priority") {
        return next.sort((a, b) => {
            const pa = PRIORITY_ORDER[a.priority?.toLowerCase()] ?? 1;
            const pb = PRIORITY_ORDER[b.priority?.toLowerCase()] ?? 1;
            return pa - pb;
        });
    }
    if (sortBy === "title") {
        return next.sort((a, b) =>
            String(a.title || "").localeCompare(String(b.title || ""))
        );
    }
    if (sortBy === "created") {
        return next.sort((a, b) => Number(b.id) - Number(a.id));
    }
    return next.sort((a, b) => {
        const ao = isOverdue(a) ? 0 : 1;
        const bo = isOverdue(b) ? 0 : 1;
        if (ao !== bo) return ao - bo;
        const pa = PRIORITY_ORDER[a.priority?.toLowerCase()] ?? 1;
        const pb = PRIORITY_ORDER[b.priority?.toLowerCase()] ?? 1;
        if (pa !== pb) return pa - pb;
        return (a.position ?? 0) - (b.position ?? 0);
    });
};

const TaskBoard = ({
    tasks = [],
    role = "admin",
    assignOptions = [],
    sortBy = "",
    onMove,
    onView,
}) => {
    const isStaff = role === "staff";
    const [savingId, setSavingId] = useState(null);
    const [pendingPause, setPendingPause] = useState(null);
    const [pendingComplete, setPendingComplete] = useState(null);
    const [collapsed, setCollapsed] = useState({ Completed: true });
    const [visibleByColumn, setVisibleByColumn] = useState({});

    const columns = useMemo(() => {
        const grouped = {};
        TASK_BOARD_COLUMNS.forEach((col) => {
            grouped[col.key] = [];
        });
        tasks.forEach((task) => {
            const key = taskBoardColumnKey(task);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(task);
        });
        Object.keys(grouped).forEach((key) => {
            grouped[key] = sortColumnTasks(grouped[key], sortBy);
        });
        return grouped;
    }, [tasks, sortBy]);

    const persistMove = async (task, columnKey, extras = {}) => {
        setSavingId(task.id);
        try {
            await onMove?.(task, columnKey, extras);
        } finally {
            setSavingId(null);
        }
    };

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId) return;
        const task = tasks.find((t) => String(t.id) === String(draggableId));
        if (!task) return;
        if (taskBoardColumnKey(task) === destination.droppableId) return;

        const nextColumn = destination.droppableId;
        if (nextColumn === "Paused") {
            setPendingPause({ task, columnKey: nextColumn });
            return;
        }
        if (nextColumn === "Completed" && isStaff) {
            setPendingComplete({ task, columnKey: nextColumn });
            return;
        }
        persistMove(task, nextColumn);
    };

    const toggleColumn = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="task-board" role="list">
                {TASK_BOARD_COLUMNS.map((column) => {
                    const columnTasks = columns[column.key] || [];
                    const overdueCount = columnTasks.filter(isOverdue).length;
                    const isCollapsed = Boolean(collapsed[column.key]);
                    const visibleCount = visibleByColumn[column.key] || COLUMN_PAGE;
                    const visibleTasks = isCollapsed
                        ? []
                        : columnTasks.slice(0, visibleCount);

                    return (
                        <Droppable droppableId={column.key} key={column.key}>
                            {(provided, snapshot) => (
                                <section
                                    className={`task-board__column ${
                                        snapshot.isDraggingOver ? "task-board__column--over" : ""
                                    } ${isCollapsed ? "task-board__column--collapsed" : ""}`}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    aria-label={`${column.label} column`}
                                    style={{ "--col-accent": column.accent }}
                                >
                                    <button
                                        type="button"
                                        className="task-board__column-head"
                                        onClick={() => toggleColumn(column.key)}
                                        aria-expanded={!isCollapsed}
                                        title={isCollapsed ? `Expand ${column.label}` : `Collapse ${column.label}`}
                                    >
                                        <span className="task-board__column-title">
                                            <span className="task-board__dot" />
                                            <span>{column.label}</span>
                                            <span className="task-board__count">{columnTasks.length}</span>
                                            {overdueCount > 0 && (
                                                <span className="task-board__overdue-count">
                                                    {overdueCount} overdue
                                                </span>
                                            )}
                                        </span>
                                        <ChevronDown
                                            size={14}
                                            className={`task-board__collapse-icon ${
                                                isCollapsed ? "is-collapsed" : ""
                                            }`}
                                            aria-hidden
                                        />
                                    </button>

                                    {!isCollapsed && (
                                        <div className="task-board__column-body">
                                            {visibleTasks.map((task, index) => {
                                                const person = isStaff
                                                    ? null
                                                    : assigneeDisplayName(task, assignOptions);

                                                return (
                                                    <Draggable
                                                        draggableId={String(task.id)}
                                                        index={index}
                                                        key={task.id}
                                                    >
                                                        {(dragProvided, dragSnapshot) => (
                                                            <article
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                className={`task-board__card ${
                                                                    dragSnapshot.isDragging
                                                                        ? "task-board__card--dragging"
                                                                        : ""
                                                                } ${
                                                                    savingId === task.id
                                                                        ? "task-board__card--saving"
                                                                        : ""
                                                                }`}
                                                            >
                                                                <div
                                                                    className="task-board__card-drag"
                                                                    {...dragProvided.dragHandleProps}
                                                                    aria-label={`Drag ${task.title || "task"}`}
                                                                >
                                                                    <span className="task-board__card-grip-wrap">
                                                                        <GripVertical
                                                                            size={14}
                                                                            className="task-board__card-grip"
                                                                            aria-hidden
                                                                        />
                                                                    </span>
                                                                    <span className="task-board__name">
                                                                        {task.title || "Untitled task"}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="task-board__card-body"
                                                                    onClick={() => onView?.(task)}
                                                                >
                                                                    <TaskCardMeta
                                                                        task={task}
                                                                        person={person}
                                                                        isStaff={isStaff}
                                                                        includeTitle={false}
                                                                    />
                                                                </button>
                                                            </article>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}

                                            {columnTasks.length === 0 && !snapshot.isDraggingOver && (
                                                <div className="task-board__empty">Drop tasks here</div>
                                            )}
                                            {snapshot.isDraggingOver && (
                                                <div className="task-board__drop-hint" aria-hidden>
                                                    Drop to move
                                                </div>
                                            )}
                                            {columnTasks.length > visibleCount && (
                                                <button
                                                    type="button"
                                                    className="task-board__more"
                                                    onClick={() =>
                                                        setVisibleByColumn((prev) => ({
                                                            ...prev,
                                                            [column.key]: visibleCount + COLUMN_PAGE,
                                                        }))
                                                    }
                                                >
                                                    Show {columnTasks.length - visibleCount} more
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {isCollapsed && (
                                        <>
                                            <div className="task-board__collapsed-drop">
                                                {snapshot.isDraggingOver ? "Drop" : columnTasks.length}
                                            </div>
                                            {provided.placeholder}
                                        </>
                                    )}
                                </section>
                            )}
                        </Droppable>
                    );
                })}
            </div>

            <PauseReasonModal
                isOpen={Boolean(pendingPause)}
                onClose={() => setPendingPause(null)}
                onConfirm={(reason) => {
                    if (!pendingPause) return;
                    persistMove(pendingPause.task, pendingPause.columnKey, { reason });
                    setPendingPause(null);
                }}
                reasons={isStaff ? STAFF_PAUSE_REASONS : undefined}
                compact={isStaff}
            />

            {isStaff && (
                <CompleteProofModal
                    isOpen={Boolean(pendingComplete)}
                    taskTitle={pendingComplete?.task?.title}
                    isRecurring={isRecurringTask(pendingComplete?.task)}
                    onClose={() => setPendingComplete(null)}
                    onConfirm={(file) => {
                        if (!pendingComplete) return;
                        persistMove(pendingComplete.task, pendingComplete.columnKey, {
                            proofFile: file || null,
                        });
                        setPendingComplete(null);
                    }}
                />
            )}
        </DragDropContext>
    );
};

export default TaskBoard;
